import { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { Base } from '@itsrever/design-system'
import { SizingProgress, SizingStatus } from '@/domain/sizing'

const { Badge, Card } = Base

type Tone = 'success' | 'info' | 'warning' | 'neutral'

const statusTones: Record<SizingStatus, Tone> = {
  WORKING: 'success',
  COLLECTING_DATA: 'info',
  NO_CLEAR_EFFECT: 'neutral',
  REDIAGNOSIS: 'warning',
  RETIRED: 'neutral'
}

const statusKeys: Record<SizingStatus, string> = {
  WORKING: 'sizing.status.working',
  COLLECTING_DATA: 'sizing.status.collecting',
  NO_CLEAR_EFFECT: 'sizing.status.no_effect',
  REDIAGNOSIS: 'sizing.status.rediagnosis',
  RETIRED: 'sizing.status.retired'
}

const statusMarks: Record<SizingStatus, string> = {
  WORKING: '●',
  COLLECTING_DATA: '⏳',
  NO_CLEAR_EFFECT: '◦',
  REDIAGNOSIS: '🔍',
  RETIRED: '✕'
}

// A model activated this cycle reads as "new" even though it is collecting
// data underneath, so the label is overridden but never the status itself.
const NEW_MARK = '✦'

export function StatusPill({
  status,
  isNew
}: {
  status: SizingStatus
  isNew?: boolean
}) {
  const { t } = useTranslation()
  return (
    <Badge
      tone={isNew ? 'info' : statusTones[status]}
      icon={isNew ? NEW_MARK : statusMarks[status]}
    >
      {isNew ? t('sizing.status.new') : t(statusKeys[status])}
    </Badge>
  )
}

export function ProgressBar({
  value,
  target
}: {
  value: number
  target: number
}) {
  const pct = Math.min(100, (value / Math.max(1, target)) * 100)
  return (
    <span className="mt-1 block h-1.5 w-full overflow-hidden rounded-full border border-solid border-neutral-200 bg-neutral-100">
      <span
        className="block h-full rounded-full bg-primary-base"
        style={{ width: `${pct}%` }}
      />
    </span>
  )
}

export function ProgressLabel({ progress }: { progress: SizingProgress }) {
  const { t } = useTranslation()
  return (
    <span className="text-xs text-neutral-500">
      {t('sizing.models.progress', {
        units: Math.min(progress.units, progress.unitsTarget),
        unitsTarget: progress.unitsTarget,
        days: Math.min(progress.days, progress.daysTarget),
        daysTarget: progress.daysTarget
      })}
    </span>
  )
}

export function StatTile({
  value,
  detail,
  label,
  sub,
  hero
}: {
  value: string
  detail?: string
  label: string
  sub?: string
  hero?: boolean
}) {
  return (
    <Card className={clsx('p-4', hero && 'border-neutral-900 bg-neutral-900')}>
      <p
        className={clsx(
          'font-semibold tabular-nums leading-tight',
          hero ? 'text-4xl text-white' : 'text-2xl'
        )}
      >
        {value}
      </p>
      {detail ? (
        <p
          className={clsx(
            'mt-0.5 text-sm font-medium tabular-nums',
            hero ? 'text-white/80' : 'text-neutral-600'
          )}
        >
          {detail}
        </p>
      ) : null}
      <p
        className={clsx('mt-1 text-xs font-semibold', hero && 'text-white/90')}
      >
        {label}
      </p>
      {sub ? (
        <p
          className={clsx(
            'mt-0.5 text-xs',
            hero ? 'text-white/60' : 'text-neutral-500'
          )}
        >
          {sub}
        </p>
      ) : null}
    </Card>
  )
}

export function BarPair({
  label,
  pct,
  maxPct,
  highlight,
  valueLabel
}: {
  label: string
  pct: number | null
  maxPct: number
  highlight: boolean
  valueLabel: string
}) {
  const width =
    pct === null ? 0 : Math.min(100, (pct / Math.max(0.001, maxPct)) * 100)
  return (
    <div className="my-0.5 grid grid-cols-[64px_1fr_64px] items-center gap-2 text-xs text-neutral-500">
      <span>{label}</span>
      <span className="h-2.5 rounded bg-neutral-100">
        <span
          className={clsx(
            'block h-full rounded',
            highlight ? 'bg-primary-base' : 'bg-neutral-300'
          )}
          style={{ width: `${width}%` }}
        />
      </span>
      <span className="text-right tabular-nums">{valueLabel}</span>
    </div>
  )
}

export function ChangeRow({
  icon,
  tone,
  title,
  children
}: PropsWithChildren<{
  icon: string
  tone: 'success' | 'info' | 'warn' | 'muted'
  title: string
}>) {
  const tones = {
    success: 'bg-success-light',
    info: 'bg-blue-50',
    warn: 'bg-amber-50',
    muted: 'bg-neutral-100'
  }
  return (
    <div className="flex items-start gap-3 border-0 border-b border-solid border-neutral-200 px-2 py-3 last:border-b-0">
      <span
        aria-hidden
        className={clsx(
          'flex size-8 flex-none items-center justify-center rounded-lg text-base',
          tones[tone]
        )}
      >
        {icon}
      </span>
      <div>
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-xs text-neutral-500">{children}</p>
      </div>
    </div>
  )
}
